/**
 * Preset Selection product template
 * Opens an "Order Details" modal on Add to Cart, then adds the product plus any
 * selected paid add-ons (Flower Insurance, Pre-paid Shipping, Proofing Experience)
 * to the cart as grouped line items — mirroring the configurator's cart behavior.
 */
(function () {
  'use strict';

  function init() {
    var section = document.querySelector('[data-preset-section]');
    var modal = document.querySelector('[data-preset-modal]');
    if (!section || !modal) return;

    var openBtn = section.querySelector('[data-preset-atc]');
    var form = modal.querySelector('[data-preset-form]');
    var closeEls = modal.querySelectorAll('[data-preset-close]');
    var errorEl = modal.querySelector('[data-preset-error]');
    var totalEl = modal.querySelector('[data-preset-total]');
    var confirmBtn = modal.querySelector('[data-preset-confirm]');
    var confirmText = modal.querySelector('[data-preset-confirm-text]');

    // --- Config from data attributes ---
    var data = section.dataset;
    var productVariantId = data.productVariantId;
    var basePriceCents = parseInt(data.productPrice, 10) || 0;
    var insuranceVariant = (data.insuranceVariant || '').trim();
    var insurancePrice = parseFloat(data.insurancePrice) || 0;
    var shippingVariant = (data.shippingVariant || '').trim();
    var shippingPrice = parseFloat(data.shippingPrice) || 0;
    var proofingVariant = (data.proofingVariant || '').trim();
    var proofingPrice = parseFloat(data.proofingPrice) || 0;

    var insuranceInputs = form.querySelectorAll('[data-insurance-input]');
    var shippingInput = form.querySelector('[data-shipping-input]');
    var proofingInput = form.querySelector('[data-proofing-input]');
    var eventDateInput = form.querySelector('[data-event-date]');

    var money = function (cents) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
    };

    var selectedInsurance = function () {
      var checked = form.querySelector('[data-insurance-input]:checked');
      return checked ? checked.value : null;
    };

    var recalcTotal = function () {
      var totalCents = basePriceCents;
      if (selectedInsurance() === 'yes' && insuranceVariant) totalCents += Math.round(insurancePrice * 100);
      if (shippingInput && shippingInput.checked) totalCents += Math.round(shippingPrice * 100);
      if (proofingInput && proofingInput.checked) totalCents += Math.round(proofingPrice * 100);
      if (totalEl) totalEl.textContent = money(totalCents);
    };

    // --- Optional variant picker (enabled per section in the theme editor) ---
    // Renders one group of choice boxes per product option; selecting a full
    // combination swaps the gallery image, price, and the variant added to cart.
    var variantPicker = section.querySelector('[data-variant-picker]');
    var variantDataEl = section.querySelector('[data-variant-data]');
    var priceDisplay = section.querySelector('[data-product-price]');
    var mainImage = section.querySelector('[data-main-image]');
    var atcLabelEl = openBtn ? openBtn.querySelector('span') : null;
    var atcDefaultLabel = atcLabelEl ? atcLabelEl.textContent.trim() : '';

    if (variantPicker && variantDataEl) {
      var variants = [];
      try { variants = JSON.parse(variantDataEl.textContent); } catch (e) { variants = []; }
      var optionInputs = variantPicker.querySelectorAll('[data-option-input]');
      var optionGroups = variantPicker.querySelectorAll('.preset-variant-option');

      var selectedOptionValues = function () {
        var values = [];
        optionGroups.forEach(function (group, i) {
          var checked = group.querySelector('[data-option-input]:checked');
          values[i] = checked ? checked.value : null;
        });
        return values;
      };

      var findVariant = function (values) {
        return variants.filter(function (v) {
          return v.options.length === values.length && v.options.every(function (opt, i) {
            return opt === values[i];
          });
        })[0];
      };

      var onOptionChange = function () {
        var variant = findVariant(selectedOptionValues());
        if (!variant) return;
        productVariantId = String(variant.id);
        basePriceCents = variant.price;
        section.dataset.productVariantId = productVariantId;
        section.dataset.productPrice = basePriceCents;
        if (priceDisplay && variant.price_formatted) priceDisplay.textContent = variant.price_formatted;
        if (mainImage && variant.image) {
          mainImage.removeAttribute('srcset');
          mainImage.src = variant.image;
        }
        if (openBtn) {
          openBtn.disabled = !variant.available;
          if (atcLabelEl) atcLabelEl.textContent = variant.available ? atcDefaultLabel : 'Sold Out';
        }
        recalcTotal();
      };

      optionInputs.forEach(function (input) { input.addEventListener('change', onOptionChange); });
    }

    // --- Modal open/close ---
    var openModal = function () {
      modal.classList.add('is-active');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      recalcTotal();
    };
    var closeModal = function () {
      modal.classList.remove('is-active');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    if (openBtn) openBtn.addEventListener('click', openModal);
    closeEls.forEach(function (el) { el.addEventListener('click', closeModal); });

    // --- Info tooltips ---
    var tooltipTriggers = modal.querySelectorAll('[data-preset-tooltip]');
    var closeTooltips = function () {
      tooltipTriggers.forEach(function (t) {
        var content = t.parentNode.querySelector('[data-preset-tooltip-content]');
        if (content) content.classList.remove('is-open');
        t.setAttribute('aria-expanded', 'false');
      });
    };
    tooltipTriggers.forEach(function (trigger) {
      var content = trigger.parentNode.querySelector('[data-preset-tooltip-content]');
      if (!content) return;
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = content.classList.contains('is-open');
        closeTooltips();
        if (!isOpen) {
          content.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
    modal.addEventListener('click', function (e) {
      if (!e.target.closest('[data-preset-tooltip]') && !e.target.closest('[data-preset-tooltip-content]')) {
        closeTooltips();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !modal.classList.contains('is-active')) return;
      if (modal.querySelector('[data-preset-tooltip-content].is-open')) {
        closeTooltips();
        return;
      }
      closeModal();
    });

    insuranceInputs.forEach(function (i) { i.addEventListener('change', recalcTotal); });
    if (shippingInput) {
      shippingInput.addEventListener('change', recalcTotal);
      // Stop the shopper if they check the prepaid box while a label is already in their cart
      shippingInput.addEventListener('change', function () {
        if (shippingInput.checked && window.warnIfLabelAlreadyInCart) window.warnIfLabelAlreadyInCart();
      });
    }
    if (proofingInput) proofingInput.addEventListener('change', recalcTotal);

    var showError = function (msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.style.display = 'block';
    };
    var clearError = function () {
      if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }
    };

    var formatDate = function (value) {
      // value is yyyy-mm-dd from the date input
      if (!value) return '';
      var parts = value.split('-');
      if (parts.length !== 3) return value;
      return parts[1] + '/' + parts[2] + '/' + parts[0]; // mm/dd/yyyy
    };

    // --- Submit ---
    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      clearError();

      // Native validation (required date / insurance choice)
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
        form.reportValidity();
        return;
      }

      if (!productVariantId) {
        showError('This product is unavailable. Please contact us for assistance.');
        return;
      }

      var groupId = 'preset-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      var insChoice = selectedInsurance();

      // Main product properties
      var properties = { '_group_id': groupId };
      if (eventDateInput && eventDateInput.value) {
        properties['Event Date'] = formatDate(eventDateInput.value);
      }
      if (insChoice === 'yes') {
        properties['Flower Insurance'] = 'Yes (+$' + insurancePrice + ')';
      } else if (insChoice === 'no') {
        properties['Flower Insurance'] = 'No';
      }
      if (shippingInput && shippingInput.checked) {
        properties['Shipping'] = 'Pre-paid shipping label';
      }
      if (proofingInput && proofingInput.checked) {
        properties['Proofing Experience'] = 'Yes (+$' + proofingPrice + ')';
      }

      var childProps = function () {
        return { '_group_id': groupId, '_parent': 'Main Product', '_line_type': 'addon' };
      };

      var items = [{ id: Number(productVariantId), quantity: 1, properties: properties }];

      if (insChoice === 'yes' && insuranceVariant) {
        items.push({ id: Number(insuranceVariant), quantity: 1, properties: childProps() });
      }
      if (shippingInput && shippingInput.checked && shippingVariant) {
        items.push({ id: Number(shippingVariant), quantity: 1, properties: childProps() });
      }
      if (proofingInput && proofingInput.checked && proofingVariant) {
        items.push({ id: Number(proofingVariant), quantity: 1, properties: childProps() });
      }

      // Loading state
      var originalText = confirmText ? confirmText.textContent : '';
      if (confirmBtn) confirmBtn.disabled = true;
      if (confirmText) confirmText.textContent = 'Adding…';

      try {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });

        if (!response.ok) {
          var err = await response.json().catch(function () { return {}; });
          throw new Error(err.description || 'Failed to add to cart.');
        }

        // Update cart count
        var cartRes = await fetch(root + 'cart.js');
        var cart = await cartRes.json();
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.dataset.count = cart.item_count;
        });

        // Refresh cart drawer markup, then open it
        try {
          var secRes = await fetch('/?sections=cart-drawer');
          var sections = await secRes.json();
          var drawerHTML = sections['cart-drawer'];
          if (drawerHTML && window.cartDrawer && window.cartDrawer.drawer) {
            var temp = document.createElement('div');
            temp.innerHTML = drawerHTML;
            var newDrawer = temp.querySelector('[data-cart-drawer]');
            if (newDrawer) window.cartDrawer.drawer.innerHTML = newDrawer.innerHTML;
          }
          // Keep the suggested-bouquets callout in sync after the drawer re-renders
          if (window.cartDrawer && typeof window.cartDrawer.recomputeSuggestedBouquets === 'function') {
            window.cartDrawer.recomputeSuggestedBouquets();
          }
        } catch (refreshErr) {
          /* non-fatal: drawer will still reflect cart on next open */
        }

        closeModal();
        if (window.cartDrawer && typeof window.cartDrawer.open === 'function') {
          window.cartDrawer.open();
        } else {
          window.location.href = root + 'cart';
        }
      } catch (error) {
        showError(error.message || 'There was an error adding your item to the cart. Please try again.');
      } finally {
        if (confirmBtn) confirmBtn.disabled = false;
        if (confirmText) confirmText.textContent = originalText;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
